import { useCallback, useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
	readEnumParam,
	readNumberParam,
	readStringParam,
	setDefaultAwareParam
} from '@/utils/search-params';
import { buildCurrentLocation } from './use-return-navigation';

// ==============================|| LIST QUERY STATE - URL ca sursă de adevăr ||============================== //
// Starea paginilor de listă (pagină, mărime pagină, căutare, sortare, filtre) trăiește
// în query string: refresh / back / bookmark restaurează exact aceeași listă.
//
// CONVENȚIA DE PAGINARE (centralizată AICI, nu prin componente):
//   - state.page e 0-based (convenția UI/DataGrid/TablePagination din proiect);
//   - în URL pagina e 1-based, lizibilă pentru om (?page=3 = a treia pagină);
//   - conversia spre API (PageNumber = page + 1) rămâne unde era deja — în
//     acțiunile fetch-hook-urilor — și nu se atinge.
//
// Config-ul de modul (defaults, parseFilters/serializeFilters, allowed*) se declară
// la NIVEL DE MODUL (src/config/list-query/*), ca o constantă — hook-ul se bazează pe
// identitatea lui stabilă.

export type SortDirection = 'asc' | 'desc';
const SORT_DIRECTIONS: readonly SortDirection[] = ['asc', 'desc'];

export interface ListQueryState<TFilters> {
	page: number; // 0-based
	pageSize: number;
	search: string;
	sortBy?: string;
	sortDirection?: SortDirection;
	filters: TFilters;
}

export interface ListQueryConfig<TFilters> {
	defaults: ListQueryState<TFilters>;
	/** Mărimile de pagină acceptate; o valoare din URL în afara listei cade pe default. */
	allowedPageSizes?: readonly number[];
	/** Câmpurile de sortare acceptate (OrderBy-urile BE); necunoscut → sortarea default. */
	allowedSortFields?: readonly string[];
	/** Citește filtrele modulului din query params (validate; invalid → default). */
	parseFilters: (params: URLSearchParams) => TFilters;
	/**
	 * Scrie filtrele modulului în query params. Trebuie să ȘTEARGĂ cheile pentru
	 * valorile default/goale (vezi setDefaultAwareParam) — nu doar să le seteze.
	 */
	serializeFilters: (params: URLSearchParams, filters: TFilters) => void;
}

// Cheile gestionate de hook — orice alt parametru din URL rămâne neatins.
const PAGE_KEY = 'page';
const PAGE_SIZE_KEY = 'pageSize';
const SEARCH_KEY = 'search';
const SORT_BY_KEY = 'sortBy';
const SORT_DIR_KEY = 'sortDir';

/** Pur: URLSearchParams → stare validată (invalid/absent → defaults). */
export function parseListQueryState<TFilters>(
	params: URLSearchParams,
	config: ListQueryConfig<TFilters>
): ListQueryState<TFilters> {
	const { defaults } = config;

	// URL 1-based → stare 0-based; valori nevalide (page=0, page=abc) → default.
	const urlPage = readNumberParam(params, PAGE_KEY, defaults.page + 1);
	const page = urlPage >= 1 ? urlPage - 1 : defaults.page;

	const rawPageSize = readNumberParam(params, PAGE_SIZE_KEY, defaults.pageSize);
	const pageSize =
		config.allowedPageSizes && !config.allowedPageSizes.includes(rawPageSize)
			? defaults.pageSize
			: rawPageSize > 0
				? rawPageSize
				: defaults.pageSize;

	const search = readStringParam(params, SEARCH_KEY, defaults.search);

	const rawSortBy = readStringParam(params, SORT_BY_KEY, '');
	const sortByValid =
		rawSortBy !== '' &&
		(!config.allowedSortFields || config.allowedSortFields.includes(rawSortBy));
	const sortBy = sortByValid ? rawSortBy : defaults.sortBy;
	// Direcția are sens doar lângă un câmp valid; altfel revine la default.
	const sortDirection = sortByValid
		? (readEnumParam(params, SORT_DIR_KEY, SORT_DIRECTIONS, undefined) ??
			defaults.sortDirection ??
			'asc')
		: defaults.sortDirection;

	return { page, pageSize, search, sortBy, sortDirection, filters: config.parseFilters(params) };
}

/** Pur: scrie starea peste parametrii EXISTENȚI (cheile străine rămân neatinse). */
export function serializeListQueryState<TFilters>(
	current: URLSearchParams,
	state: ListQueryState<TFilters>,
	config: ListQueryConfig<TFilters>
): URLSearchParams {
	const { defaults } = config;
	const params = new URLSearchParams(current);

	// stare 0-based → URL 1-based; pagina default nu apare în URL.
	setDefaultAwareParam(params, PAGE_KEY, state.page + 1, defaults.page + 1);
	setDefaultAwareParam(params, PAGE_SIZE_KEY, state.pageSize, defaults.pageSize);
	setDefaultAwareParam(params, SEARCH_KEY, state.search, defaults.search);
	// Sortarea se scrie doar când diferă (câmp SAU direcție) de sortarea default a
	// modulului — la module cu default non-gol (ex. „înregistrat la, desc") și doar
	// direcția schimbată trebuie să ajungă în URL, deci scriem ambele chei împreună.
	const sortIsDefault =
		state.sortBy === defaults.sortBy && state.sortDirection === defaults.sortDirection;
	if (sortIsDefault || !state.sortBy) {
		params.delete(SORT_BY_KEY);
		params.delete(SORT_DIR_KEY);
	} else {
		params.set(SORT_BY_KEY, state.sortBy);
		setDefaultAwareParam(params, SORT_DIR_KEY, state.sortDirection, undefined);
	}
	config.serializeFilters(params, state.filters);
	return params;
}

export interface UseListQueryStateReturn<TFilters> {
	state: ListQueryState<TFilters>;
	setPage: (page: number) => void;
	setPageSize: (pageSize: number) => void;
	setSearch: (search: string) => void;
	setFilter: <K extends keyof TFilters>(key: K, value: TFilters[K]) => void;
	setFilters: (filters: TFilters) => void;
	setSorting: (sortBy: string | undefined, sortDirection?: SortDirection) => void;
	/** Toggle asc→desc pe același câmp — convenția handleSort din paginile existente. */
	toggleSort: (field: string) => void;
	resetFilters: () => void;
	resetAll: () => void;
	/** Locația curentă completă (pathname + query + hash) — de pasat ca `state.from`. */
	getReturnLocation: () => string;
}

export function useListQueryState<TFilters>(
	config: ListQueryConfig<TFilters>
): UseListQueryStateReturn<TFilters> {
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();

	const state = useMemo(
		() => parseListQueryState(searchParams, config),
		// config e constantă de modul (identitate stabilă) — vezi comentariul de sus.
		[searchParams, config]
	);

	// setSearchParams NU compune apelurile din același tick: forma funcțională primește
	// parametrii de la RANDARE, nu rezultatul apelului precedent (vezi useSearchParams în
	// react-router). Două settere chemate în același handler (ex. MantisPagination:
	// setItems urmat de setPage(0)) s-ar suprascrie reciproc — al doilea ar reconstrui
	// URL-ul din starea veche și ar anula prima scriere. De aceea ținem ultima scriere
	// din tick într-un ref și o folosim ca BAZĂ pentru commit-ul următor.
	const pendingParamsRef = useRef<URLSearchParams | null>(null);
	// La randare, adevărul redevine URL-ul (navigarea a aterizat) — ref-ul se golește.
	pendingParamsRef.current = null;

	// Toate schimbările frecvente folosesc replace: istoricul browserului rămâne curat,
	// iar Back din pagina de detalii se întoarce la ULTIMA stare a listei, nu la fiecare
	// tastă/click intermediar. Scrierea se sare când nimic nu s-a schimbat (anti-buclă).
	// Commit-ul primește un UPDATER (stare curentă → stare nouă), nu o stare gata făcută:
	// starea de la randare poate fi deja veche în momentul apelului (debounce, apeluri
	// consecutive) — updater-ul se aplică pe starea parsată din baza REALĂ.
	const commit = useCallback(
		(updater: (current: ListQueryState<TFilters>) => ListQueryState<TFilters>): void => {
			setSearchParams(
				(routerParams) => {
					// Baza e URL-ul LIVE, nu snapshot-ul router-ului: unele pagini (ex. harta
					// din Proiecte) scriu parametri prin history.replaceState, pe care router-ul
					// nu îi vede — altfel un commit i-ar șterge din URL.
					const liveParams =
						typeof window === 'undefined'
							? routerParams
							: new URLSearchParams(window.location.search);
					const base = pendingParamsRef.current ?? liveParams;
					const next = updater(parseListQueryState(base, config));
					const serialized = serializeListQueryState(base, next, config);
					const result =
						serialized.toString() === base.toString() ? base : serialized;
					pendingParamsRef.current = result;
					return result;
				},
				{ replace: true }
			);
		},
		[setSearchParams, config]
	);

	const setPage = useCallback(
		(page: number) => commit((s) => ({ ...s, page: Math.max(0, page) })),
		[commit]
	);

	// Schimbările de criterii (căutare / filtre / mărime pagină / sortare) invalidează
	// poziția curentă → înapoi la prima pagină.
	const setPageSize = useCallback(
		(pageSize: number) =>
			commit((s) => ({ ...s, pageSize, page: config.defaults.page })),
		[commit, config]
	);

	const setSearch = useCallback(
		(search: string) => commit((s) => ({ ...s, search, page: config.defaults.page })),
		[commit, config]
	);

	const setFilters = useCallback(
		(filters: TFilters) =>
			commit((s) => ({ ...s, filters, page: config.defaults.page })),
		[commit, config]
	);

	const setFilter = useCallback(
		<K extends keyof TFilters>(key: K, value: TFilters[K]) =>
			commit((s) => ({
				...s,
				filters: { ...s.filters, [key]: value },
				page: config.defaults.page
			})),
		[commit, config]
	);

	const setSorting = useCallback(
		(sortBy: string | undefined, sortDirection?: SortDirection) =>
			commit((s) => ({
				...s,
				sortBy,
				sortDirection: sortBy ? (sortDirection ?? 'asc') : undefined,
				page: config.defaults.page
			})),
		[commit, config]
	);

	const toggleSort = useCallback(
		(field: string) =>
			commit((s) => ({
				...s,
				sortBy: field,
				sortDirection:
					s.sortBy === field && s.sortDirection === 'asc' ? 'desc' : 'asc',
				page: config.defaults.page
			})),
		[commit, config]
	);

	const resetFilters = useCallback(
		() =>
			commit((s) => ({
				...s,
				search: config.defaults.search,
				filters: config.defaults.filters,
				page: config.defaults.page
			})),
		[commit, config]
	);

	const resetAll = useCallback(() => commit(() => config.defaults), [commit, config]);

	const getReturnLocation = useCallback(() => buildCurrentLocation(location), [location]);

	return {
		state,
		setPage,
		setPageSize,
		setSearch,
		setFilter,
		setFilters,
		setSorting,
		toggleSort,
		resetFilters,
		resetAll,
		getReturnLocation
	};
}
